import React, { useRef, useEffect } from 'react';
import {
    Card, CardContent, Typography, Button, Box,
    LinearProgress, Alert, Collapse, Divider, Paper
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SanitizedHtmlRenderer from './SanitizedHtmlRenderer';

// Helper to safely extract HTML content
const getHtmlContent = (data) => {
    if (!data) return ''; 
    if (typeof data === 'string') return data;
    if (data.text) return data.text;
    return '';
};

// Helper to get plain text for comparisons
const getPlainText = (data) => {
    const html = getHtmlContent(data);
    return html.replace(/<[^>]*>?/gm, '');
};

const QuizCard = ({
    question,
    questionNumber,
    totalQuestions,
    selectedValue,
    isRevealed,
    allQuestionsAnswered,
    onAnswerSelect,
    onCheckAnswer,
    onSkip,
    onNextQuestion,
    onFinishQuiz
}) => {
    const cardTopRef = useRef(null);
    const explanationRef = useRef(null);

    // Scroll to top on new question
    useEffect(() => {
        if (cardTopRef.current) {
            setTimeout(() => {
                const element = cardTopRef.current;
                const headerOffset = 80;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    
                window.scrollTo({ top: offsetPosition, behavior: "smooth" });
            }, 100);
        }
    }, [question]);

    // Scroll to explanation on reveal
    useEffect(() => {
        if (isRevealed && explanationRef.current) {
            setTimeout(() => {
                explanationRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 200);
        }
    }, [isRevealed]);

    if (!question) return null; 

    const progress = totalQuestions > 0 ? (questionNumber / totalQuestions) * 100 : 0;
    const correctAnswerText = getPlainText(question.answer);
    const selectedText = selectedValue ? getPlainText(selectedValue) : '';
    const isCorrect = selectedValue !== null && selectedText === correctAnswerText;
    const handleNextOrFinish = allQuestionsAnswered ? onFinishQuiz : onNextQuestion;

    // --- NEW: Helper to determine the style of the option button ---
    const getOptionStyles = (optionValue) => {
        const isSelected = optionValue === selectedText;
        const isTheCorrectAnswer = optionValue === correctAnswerText;
        
        let styles = {
            p: 2,
            mb: 1.5,
            borderRadius: 2,
            border: '2px solid',
            cursor: isRevealed ? 'default' : 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            position: 'relative'
        };

        if (isRevealed) {
            if (isTheCorrectAnswer) {
                // Style for the correct answer (Green)
                return { 
                    ...styles, 
                    borderColor: 'success.main', 
                    bgcolor: 'success.light',
                    color: 'success.dark',
                    '&:hover': { bgcolor: 'success.light' } // Disable hover effect
                };
            }
            if (isSelected && !isTheCorrectAnswer) {
                // Style for wrong selection (Red)
                return { 
                    ...styles, 
                    borderColor: 'error.main', 
                    bgcolor: 'error.light',
                    color: 'error.dark',
                    '&:hover': { bgcolor: 'error.light' }
                };
            }
            // Style for other unselected options
            return { 
                ...styles, 
                borderColor: 'divider', 
                opacity: 0.6 
            };
        }

        // Normal Quiz Mode (Not Revealed)
        if (isSelected) {
            return { 
                ...styles, 
                borderColor: 'primary.main', 
                bgcolor: 'action.selected',
                transform: 'scale(1.01)',
                boxShadow: 2
            };
        }

        // Default Unselected State
        return { 
            ...styles, 
            borderColor: 'divider',
            '&:hover': { 
                borderColor: 'primary.light', 
                bgcolor: 'action.hover' 
            }
        };
    };

    return (
        <Card raised ref={cardTopRef}>
            <CardContent>
                <Typography variant="h5" component="div" gutterBottom>
                    Question {questionNumber} of {totalQuestions}
                </Typography>
                <Box sx={{ width: '100%', mb: 2 }}>
                    <LinearProgress variant="determinate" value={progress} />
                </Box>
                
                <Box sx={{ mb: 3 }}>
                    <SanitizedHtmlRenderer htmlContent={getHtmlContent(question.question)} />
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* --- NEW: Option Buttons Loop --- */}
                <Box role="radiogroup">
                    {(question.options || []).map((option, index) => {
                        const optionHtml = getHtmlContent(option);
                        const optionValue = getPlainText(option);
                        const isTheCorrectAnswer = optionValue === correctAnswerText;
                        const isSelected = optionValue === selectedText;

                        return (
                            <Paper
                                key={index}
                                variant="outlined"
                                sx={getOptionStyles(optionValue)}
                                onClick={() => !isRevealed && onAnswerSelect(optionValue)}
                                role="radio"
                                aria-checked={isSelected}
                            >
                                {/* Circle/Check Icon Area */}
                                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                                    {isRevealed && isTheCorrectAnswer ? (
                                        <CheckCircleIcon color="success" />
                                    ) : isRevealed && isSelected && !isTheCorrectAnswer ? (
                                        <CancelIcon color="error" />
                                    ) : (
                                        <Box sx={{
                                            width: 20, 
                                            height: 20, 
                                            borderRadius: '50%', 
                                            border: '2px solid',
                                            borderColor: isSelected ? 'primary.main' : 'text.disabled',
                                            bgcolor: isSelected ? 'primary.main' : 'transparent'
                                        }} />
                                    )}
                                </Box>

                                {/* Option Text */}
                                <Box sx={{ flexGrow: 1 }}>
                                    <SanitizedHtmlRenderer htmlContent={optionHtml} />
                                </Box>
                            </Paper>
                        );
                    })}
                </Box>

                <Collapse in={isRevealed}>
                    <Box ref={explanationRef}>
                        <Alert severity={isCorrect ? "success" : "error"} sx={{ mt: 2, fontSize: '1rem' }}>
                            {isCorrect ? <strong>Correct!</strong> : <strong>Incorrect.</strong>}
                        </Alert>
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Explanation:
                            </Typography>
                            <SanitizedHtmlRenderer htmlContent={getHtmlContent(question.explanation)} />
                        </Box>
                    </Box>
                </Collapse>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                    {isRevealed ? (
                        <Box /> 
                    ) : (
                        <Button variant="outlined" onClick={onSkip}>
                            Skip for Now
                        </Button>
                    )}

                    {isRevealed ? (
                        <Button variant="contained" size="large" onClick={handleNextOrFinish}>
                            {allQuestionsAnswered ? 'Finish Quiz' : 'Next Question'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            size="large"
                            onClick={onCheckAnswer}
                            disabled={!selectedValue}
                        >
                            Check Answer
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default QuizCard;
